<script setup lang="ts">
  import { onMounted, ref } from 'vue';
  import { storeToRefs } from 'pinia';
  import isElectron from 'is-electron';
  import LoadingPage from '@/pages/LoadingPage.vue';
  import ReloadPrompt from '@/components/ReloadPrompt.vue';
  import PotaParksInitToast from '@/components/PotaParksInitToast.vue';
  import { useProfileStore } from '@/stores/ProfileStore';
  import { useSyncStore } from '@/stores/SyncStore';
  import { useLogbookStore } from '@/stores/LogbookStore';
  import { useSettingStore } from '@/stores/SettingStore';
  import { ensureIndexes } from "@/repos/PouchDB";
  import { useRigControl } from '@/composables/useRigControl';
  import { useQrzApiIntegrationStore } from './stores/QrzIntegrationStore';
  import { useFt8Store } from '@/stores/Ft8Store';

  import type { RouteLocationNormalized } from 'vue-router';
  import { router, NEW_PROFILE_PAGE_ROUTE_NAME, SYNC_PAGE_ROUTE_NAME } from '@/router/router';
  import { useNetworkStatus } from '@/composables/useNetworkStatus';
  import { hamrsClientLogger } from '@/utils/loggers';
  import { captureException } from '@/utils/sentry';
  import { requireSync, hamrsDotAppURL, haveLoadingPage } from '@/config';
  import type { ProfileInterface } from '@/interfaces';
  import { usePotaParksSync } from '@/composables/usePotaParksSync';
  import { APP_VERSION } from '@/constants';

  const profileStore = useProfileStore();
  const settingStore = useSettingStore();
  const logbookStore = useLogbookStore();
  const qrzApiIntegrationStore = useQrzApiIntegrationStore();
  const syncStore = useSyncStore();
  const ft8Store = useFt8Store(); // Initialize FT8 store early to set up IPC listeners
  const { sync } = storeToRefs(syncStore);
  const { isOnline } = useNetworkStatus();
  const { testConnection } = useRigControl();

  // Initialize POTA parks sync handling
  usePotaParksSync();

  const isDev = process.env.NODE_ENV === 'development';
  const loading = ref(haveLoadingPage);

  hamrsClientLogger('Online:', isOnline.value);
  hamrsClientLogger('Require Sync:', requireSync);
  hamrsClientLogger('Is Electron:', isElectron());

  // Log FT8 initialization status
  if (isElectron() && ft8Store.isSupported) {
    hamrsClientLogger('FT8 Integration:', ft8Store.enabled ? 'Enabled' : 'Disabled');
    if (ft8Store.enabled) {
      hamrsClientLogger('FT8 Connection Status:', ft8Store.connectionStatus);
    }
  }

  // Ensure that new users without profiles can only go to the NewProfilePage or
  // the SyncPage.
  function redirectToNewProfilePageIfFirstTime(to: RouteLocationNormalized) {
    if (profileStore.profiles.length === 0 &&
      to.name !== NEW_PROFILE_PAGE_ROUTE_NAME &&
      to.name !== SYNC_PAGE_ROUTE_NAME
    ) {
      return {
        name: NEW_PROFILE_PAGE_ROUTE_NAME,
        query: { firstTimeSetup: 'true' }
      };
    }
  }

  function setCurrentProfile(profile: ProfileInterface) {
    profileStore.activateProfile(profile);
  }
  async function checkForRigControl() {
    if (isElectron() || isDev) {
      // Rig control is a non-essential enhancement. It must never be able to
      // reject this promise: it runs inside the bootstrap Promise.all, and a
      // rejection there would leave the app stuck on the loading screen.
      try {
        await testConnection();
      } catch (error) {
        captureException(error, { feature: 'rig-control', phase: 'bootstrap' });
      }
    }
  }
  async function initializeDatabase() {
    // Start syncing the database and look for a sync token.
    await syncStore.load();
    await ensureIndexes();
    // If this app is running in the mode that needs sync (the web client), and
    // they don't already have a sync token in place, then we need to get one. We
    // redirect them to the sign up page on hamrs.app and they'll come back here
    // with a token in a query param.
    if (requireSync && !sync.value.hamrs?.apiKey) {

      const token = new URL(window.location.toString()).searchParams.get('token');
      if (!token) {
        return { redirectToGetAccount: true };
      }

      await syncStore.create(token);
    }

    // We have to do an initial load of the profiles so we can handle redirection.
    await profileStore.fetchProfiles();

    // Ensure that there's always an active profile. If they've previously
    // configured one in their local settings then use that, otherwise just use
    // their first profile.
    if (profileStore.profiles.length > 0) {
      if (settingStore.activeProfileID) {
        const profile = profileStore.findOne(settingStore.activeProfileID);
        if (profile !== undefined) { setCurrentProfile(profile); }
      } else {
        setCurrentProfile(profileStore.profiles[0]);
      }
    }

    // After the profileStore is populated we can register a navigation guard to
    // keep new users on the setup pages. If we register this guard too soon then
    // existing users will always start on the first time setup experience.
    router.beforeEach(redirectToNewProfilePageIfFirstTime);

    // However, since we've waited this long to put the guard in place then we
    // have to invoke it once ourselves.
    return {
      routerRedirect: redirectToNewProfilePageIfFirstTime({
        // Add necessary properties here
      } as RouteLocationNormalized)
    };
  }

  function sleep(ms: number) {
    return new Promise<void>((resolve) => {
      setTimeout(() => { resolve(); }, ms);
    });
  }

  onMounted(async () => {

    // Start initializing the database and concurrently start a 2 second timer.
    // This lets us have a minimum time before we turn off the loading screen so
    // it's not a weird flash.
    qrzApiIntegrationStore.testApiKey();
    Promise.all([
      initializeDatabase(),
      // Templates aren't needed for first paint; a failure here must not strand the
      // loading screen, so it can't be allowed to reject the bootstrap Promise.all.
      logbookStore.fetchLogbookTemplates().catch((error) => {
        captureException(error, { feature: 'logbook-templates', phase: 'bootstrap' });
      }),
      checkForRigControl(),
      sleep(1000),
    ]).then(([{ redirectToGetAccount, routerRedirect }]) => {
      if (redirectToGetAccount) {
        window.location.href = `${hamrsDotAppURL}/users/sign_in?logger_return=true`;
        return;
      }

      if (routerRedirect) {
        router.push(routerRedirect);
      }

      // Check for version updates after initialization
      settingStore.checkForVersionUpdate(APP_VERSION);

      loading.value = false;
    }).catch((error) => {
      // If initialization rejects (e.g. a transient IndexedDB open/delete race),
      // never leave the user stuck on "Stand by" - log it and render the app so it's
      // at least usable rather than a dead loading screen.
      captureException(error, { feature: 'bootstrap', phase: 'initialize-database' });
      hamrsClientLogger('App initialization failed; rendering anyway.', error);
      loading.value = false;
    });
  });
</script>

<template>
  <LoadingPage v-if="loading" />
  <router-view v-if="!loading" />
  <PotaParksInitToast />
  <ReloadPrompt />
</template>
